/**
 * TheoryConstants.js
 * Definitions for Scale Intervals, Chord Formulas, Progression Templates, Rhythm/Groove Patterns, and Instrument Physics.
 * Source of Truth for MusicEngine calculations.
 */

export const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const FLAT_TO_SHARP = {
    'Db': 'C#',
    'Eb': 'D#',
    'Gb': 'F#',
    'Ab': 'G#',
    'Bb': 'A#',
    'Cb': 'B',
    'Fb': 'E'
};

export const normalizeKey = (key) => {
    if (!key) return 'C';
    return FLAT_TO_SHARP[key] || key;
};

// 1. THE NEW GROUPED STRUCTURE (For the Phase 3 UI)
export const SCALES = {
    MAJOR_MODES: [
        { name: 'Ionian (Major)', intervals: [0, 2, 4, 5, 7, 9, 11] },
        { name: 'Dorian', intervals: [0, 2, 3, 5, 7, 9, 10] },
        { name: 'Phrygian', intervals: [0, 1, 3, 5, 7, 8, 10] },
        { name: 'Lydian', intervals: [0, 2, 4, 6, 7, 9, 11] },
        { name: 'Mixolydian', intervals: [0, 2, 4, 5, 7, 9, 10] },
        { name: 'Aeolian (Minor)', intervals: [0, 2, 3, 5, 7, 8, 10] },
        { name: 'Locrian', intervals: [0, 1, 3, 5, 6, 8, 10] }
    ],
    PENTATONIC: [
        { name: 'Major Pentatonic', intervals: [0, 2, 4, 7, 9] },
        { name: 'Minor Pentatonic', intervals: [0, 3, 5, 7, 10] },
        { name: 'Blues', intervals: [0, 3, 5, 6, 7, 10] }
    ],
    EXOTIC: [
        { name: 'Harmonic Minor', intervals: [0, 2, 3, 5, 7, 8, 11] },
        { name: 'Melodic Minor', intervals: [0, 2, 3, 5, 7, 9, 11] },
        { name: 'Whole Tone', intervals: [0, 2, 4, 6, 8, 10] }
    ]
};

// 2. THE LEGACY FLAT LIST (Critical for backward compatibility with Phase 1 Engine)
// We combine all groups into one simple array so the current MusicEngine doesn't break.
export const SCALE_INTERVALS = {};

// Transform SCALES array back to key-value object for engine compatibility
const nameToKey = (name) => {
    if (name === 'Ionian (Major)') return 'major';
    if (name === 'Aeolian (Minor)') return 'minor';
    if (name === 'Major Pentatonic') return 'pentatonic_major';
    if (name === 'Minor Pentatonic') return 'pentatonic_minor';
    if (name === 'Blues') return 'blues';
    if (name === 'Harmonic Minor') return 'harmonic_minor';
    if (name === 'Melodic Minor') return 'melodic_minor';
    if (name === 'Whole Tone') return 'whole_tone';
    return name.toLowerCase().replace(/\s+/g, '_');
};

// Populate SCALE_INTERVALS from SCALES
Object.values(SCALES).flat().forEach(scale => {
    SCALE_INTERVALS[nameToKey(scale.name)] = scale.intervals;
});

// Add back legacy scales that might not be in the new groups but needed for engine
SCALE_INTERVALS['diminished'] = [0, 2, 3, 5, 6, 8, 9, 11];
SCALE_INTERVALS['augmented'] = [0, 3, 4, 7, 8, 11];
SCALE_INTERVALS['hirajoshi'] = [0, 2, 3, 7, 8];
SCALE_INTERVALS['in_sen'] = [0, 1, 5, 7, 10];
SCALE_INTERVALS['iwato'] = [0, 1, 5, 6, 10];
SCALE_INTERVALS['kumoi'] = [0, 2, 3, 7, 9];
SCALE_INTERVALS['pelog'] = [0, 1, 3, 7, 8];
SCALE_INTERVALS['bhairav'] = [0, 1, 4, 5, 7, 8, 11];


export const CHORD_FORMULAS = {
    major: [0, 4, 7],
    minor: [0, 3, 7],
    diminished: [0, 3, 6],
    augmented: [0, 4, 8],
    sus2: [0, 2, 7],
    sus4: [0, 5, 7],
    power: [0, 7], // 1-5
    maj7: [0, 4, 7, 11],
    min7: [0, 3, 7, 10],
    dom7: [0, 4, 7, 10],
    dim7: [0, 3, 6, 9],
    half_dim7: [0, 3, 6, 10],
    minmaj7: [0, 3, 7, 11],
    add9: [0, 4, 7, 14],
    min9: [0, 3, 7, 10, 14],
    maj9: [0, 4, 7, 11, 14]
};

// --- DYNAMIC TEMPLATES SYSTEM ---
// Replaces static arrays with "Smart Objects" containing contextual metadata.
// Supports backward compatibility via a wrapper or smart consumption in MusicEngine.

export const PROGRESSION_TEMPLATES = {
    pop_standard: {
        variations: [
            { seq: [[0, 4, 5, 3]], weight: 1.0, conditions: { intensityMax: 0.7 } },
            { seq: [[0, 5, 3, 4]], weight: 0.8 },
            { seq: [[5, 3, 0, 4]], weight: 0.5, conditions: { section: 'BRIDGE' } },
            { seq: [[0, 3, 0, 4]], weight: 0.7, conditions: { intensityMin: 0.4 } }, // Plagal feel
            { seq: [[0, 2, 3, 4]], weight: 0.6 }, // Stepwise rise
            { seq: [[5, 0, 3, 4]], weight: 0.6 }, // Relative minor start
            { seq: [[0, 4, 5, 2]], weight: 0.4, conditions: { section: 'VERSE' } }, // Soft ending
            { seq: [[0, 5, 1, 4]], weight: 0.5 }, // 50s progression
            { seq: [[3, 0, 3, 4]], weight: 0.5, conditions: { intensityMin: 0.6 } }
        ],
        tags: ['pop', 'happy', 'simple']
    },
    rock_power: {
        variations: [
            { seq: [[0, 3, 4]], weight: 1.0 },
            { seq: [[0, 2, 3]], weight: 0.8 },
            { seq: [[0, 6, 5]], weight: 0.6, conditions: { intensityMin: 0.6 } },
            { seq: [[0, 2, 6, 5]], weight: 0.7, conditions: { section: 'CHORUS' } }, // Mixolydian VII
            { seq: [[0, 3, 0, 6]], weight: 0.7 }, // AC/DC style
            { seq: [[5, 3, 0, 4]], weight: 0.5, conditions: { section: 'BRIDGE' } },
            { seq: [[0, 5, 3, 6]], weight: 0.6 } // Darker rock
        ],
        tags: ['rock', 'driving', 'power']
    },
    punk: {
        variations: [
            { seq: [[0, 3, 4]], weight: 1.0 },
            { seq: [[0, 4, 5, 3]], weight: 0.7 },
            { seq: [[0, 2, 3]], weight: 0.5 },
            { seq: [[0, 3, 0, 4]], weight: 0.8, conditions: { intensityMax: 0.8 } },
            { seq: [[0, 4, 3, 4]], weight: 0.6 },
            { seq: [[0, 6, 5]], weight: 0.5, conditions: { section: 'INTRO' } }
        ],
        tags: ['punk', 'fast', 'aggressive']
    },
    jazz_standard: {
        variations: [
            { seq: [[1, 4, 0]], weight: 1.0 }, // ii-V-I
            { seq: [[1, 4, 0, 5]], weight: 0.9 }, // ii-V-I-vi
            { seq: [[0, 5, 1, 4]], weight: 0.8 }, // I-vi-ii-V
            { seq: [[2, 5, 1, 4]], weight: 0.7 }, // iii-vi-ii-V
            { seq: [[0, 3, 2, 5]], weight: 0.6 }, // I-IV-iii-vi
            { seq: [[1, 4, 2, 5]], weight: 0.6 }, // ii-V-iii-vi (Turnaround)
            { seq: [[0, 1, 2, 3, 4]], weight: 0.3, conditions: { section: 'OUTRO' } }, // Chromatic/Cycle
            { seq: [[0, 4, 3, 6]], weight: 0.5 } // Backdoor ii-Vish
        ],
        tags: ['jazz', 'swing', 'complex']
    },
    jazz_minor: {
        variations: [
            { seq: [[1, 4, 0]], weight: 1.0 },
            { seq: [[0, 3, 4, 0]], weight: 0.8 },
            { seq: [[0, 5, 1, 4]], weight: 0.7 }, // i-VI-ii-V
            { seq: [[0, 1, 4]], weight: 0.6 } // i-ii-V
        ],
        tags: ['jazz', 'minor', 'dark']
    },
    modal_jazz: {
        variations: [
            { seq: [[1, 2]], weight: 1.0 },
            { seq: [[0, 1]], weight: 0.8 },
            { seq: [[0, 3]], weight: 0.7 }, // I - IV vamp
            { seq: [[0, 6]], weight: 0.6 }  // I - bVII
        ],
        tags: ['jazz', 'modal']
    },
    epic_heroic: {
        variations: [
            { seq: [[0, 5, 3, 4]], weight: 1.0 },
            { seq: [[0, 2, 0, 4]], weight: 0.8 },
            { seq: [[5, 3, 0, 4]], weight: 0.7, conditions: { section: 'CLIMAX' } },
            { seq: [[0, 2, 5, 4]], weight: 0.6 },
            { seq: [[0, 6, 5, 3]], weight: 0.5, conditions: { intensityMin: 0.8 } } // Dramatic descent
        ],
        tags: ['cinematic', 'epic']
    },
    dark_tension: {
        variations: [
            { seq: [[0, 1]], weight: 1.0 }, // i-bii
            { seq: [[0, 6]], weight: 0.8 }, // i-vii
            { seq: [[0, 4]], weight: 0.6 }, // i-v
            { seq: [[0, 2]], weight: 0.5 }, // i-biii (Chromatic mediant)
            { seq: [[0, 3, 0, 1]], weight: 0.5 }
        ],
        tags: ['cinematic', 'horror', 'tension']
    },
    wonder: {
        variations: [
            { seq: [[0, 3, 4]], weight: 1.0 },
            { seq: [[0, 2, 5]], weight: 0.8 },
            { seq: [[0, 5, 3, 4]], weight: 0.6 },
            { seq: [[3, 2, 1, 4]], weight: 0.5 }
        ],
        tags: ['pop', 'wonder']
    },
    house_loop: {
        variations: [
            { seq: [[0, 6, 5, 6]], weight: 1.0 },
            { seq: [[0, 2, 0, 2]], weight: 0.8 },
            { seq: [[0, 3]], weight: 0.7, conditions: { section: 'INTRO' } },
            { seq: [[5, 0]], weight: 0.6 },
            { seq: [[1, 4]], weight: 0.5 }
        ],
        tags: ['electronic', 'house']
    },
    trance_uplifting: {
        variations: [
            { seq: [[0, 3, 4, 5]], weight: 1.0 },
            { seq: [[5, 3, 0, 4]], weight: 0.9 },
            { seq: [[0, 4, 5, 3]], weight: 0.8 },
            { seq: [[5, 0, 3, 4]], weight: 0.7 }
        ],
        tags: ['electronic', 'trance']
    },
    techno_dark: {
        variations: [
            { seq: [[0, 0, 0, 1]], weight: 1.0 },
            { seq: [[0, 6, 0, 6]], weight: 0.8 },
            { seq: [[0, 2, 0, 2]], weight: 0.7 }, // Minor 3rd osc
            { seq: [[0, 4]], weight: 0.6 } // i-v
        ],
        tags: ['electronic', 'techno']
    },
    medieval_modal: {
        variations: [
            { seq: [[0, 6, 0]], weight: 1.0 },
            { seq: [[0, 3, 0]], weight: 0.8 },
            { seq: [[0, 5, 4]], weight: 0.7 }
        ],
        tags: ['classical', 'medieval']
    },
    christmas_classic: {
        variations: [
            { seq: [[0, 1, 1, 4]], weight: 1.0 },
            { seq: [[0, 5, 1, 4]], weight: 0.8 }
        ],
        tags: ['seasonal', 'christmas']
    },
    emotional_ballad: {
        variations: [
            { seq: [[0, 4, 5, 3]], weight: 1.0 },
            { seq: [[0, 2, 3, 4]], weight: 0.9 },
            { seq: [[5, 0, 3, 4]], weight: 0.7 }
        ],
        tags: ['pop', 'ballad']
    },
    royal_fanfare: {
        variations: [
            { seq: [[0, 4, 0, 3]], weight: 1.0 },
            { seq: [[0, 4, 0, 4]], weight: 0.8 }
        ],
        tags: ['classical', 'fanfare']
    },
    blues_12_bar: {
        variations: [
            { seq: [[0, 0, 0, 0, 3, 3, 0, 0, 4, 3, 0, 4]], weight: 1.0 }
        ],
        tags: ['blues', 'jazz']
    },
    neo_soul: {
        variations: [
            { seq: [[3, 2, 1, 4]], weight: 1.0 },
            { seq: [[1, 4, 2, 5]], weight: 0.8 }
        ],
        tags: ['jazz', 'soul']
    },
    gospel_turnaround: {
        variations: [
            { seq: [[0, 5, 1, 4]], weight: 1.0 },
            { seq: [[0, 2, 5, 4]], weight: 0.8 }
        ],
        tags: ['jazz', 'gospel']
    },
    lofi_nostalgia: {
        variations: [
            { seq: [[0, 4, 5, 3]], weight: 1.0 },
            { seq: [[0, 2, 3, 4]], weight: 0.9 },
            { seq: [[3, 2, 1, 4]], weight: 0.8 }
        ],
        tags: ['lofi', 'chill']
    },
    lofi_chill: {
        variations: [
            { seq: [[1, 4]], weight: 1.0 },
            { seq: [[3, 2]], weight: 0.9 },
            { seq: [[5, 4, 0, 0]], weight: 0.8 }
        ],
        tags: ['lofi', 'chill']
    },
    lofi_jazz: {
        variations: [
            { seq: [[1, 4, 0, 5]], weight: 1.0 },
            { seq: [[3, 6, 2, 5]], weight: 0.9 },
            { seq: [[1, 1, 4, 4]], weight: 0.8 }
        ],
        tags: ['lofi', 'jazz']
    },
    americana_folk: {
        variations: [
            { seq: [[0, 3, 0, 4]], weight: 1.0 },
            { seq: [[0, 5, 3, 0]], weight: 0.8 }
        ],
        tags: ['folk', 'country']
    }
};

// Functional Harmony Substitution Table
export const FUNCTIONAL_SUBSTITUTIONS = {
    // TONIC (I) Substitutes: vi (5), iii (2)
    0: [5, 2],
    // SUPERTONIC (ii) Substitutes: IV (3) - Predominant
    1: [3],
    // MEDIANT (iii) Substitutes: I (0), vi (5) - Tonic function usually
    2: [0, 5],
    // SUBDOMINANT (IV) Substitutes: ii (1)
    3: [1],
    // DOMINANT (V) Substitutes: vii dim (6)
    4: [6],
    // SUBMEDIANT (vi) Substitutes: I (0)
    5: [0],
    // LEADING TONE (vii) Substitutes: V (4)
    6: [4]
};

export const GROOVE_TEMPLATES = {
    standard_4_4: {
        pattern: [1.2, 0.6, 0.8, 0.6, 1.1, 0.6, 0.9, 0.6, 1.2, 0.6, 0.8, 0.6, 1.1, 0.6, 0.9, 0.6],
        tags: ['default']
    },
    motorik: {
        pattern: [1.0, 0.9, 1.0, 0.9, 1.0, 0.9, 1.0, 0.9, 1.0, 0.9, 1.0, 0.9, 1.0, 0.9, 1.0, 0.9],
        tags: ['rock', 'electronic']
    },
    waltz: {
        pattern: [1.2, 0.5, 0.5, 0.5, 0.9, 0.5, 0.5, 0.5, 0.9, 0.5, 0.5, 0.5],
        tags: ['classical', 'folk']
    },
    swing_jazz: {
        pattern: [1.1, 0.0, 0.7, 0.5, 1.0, 0.0, 0.8, 0.5, 1.1, 0.0, 0.7, 0.5, 1.0, 0.0, 0.8, 0.5],
        tags: ['jazz', 'swing']
    },
    trap_beat: {
        pattern: [1.2, 0.4, 0.4, 0.4, 0.4, 0.4, 1.1, 0.4, 1.2, 0.4, 0.4, 0.4, 0.4, 0.4, 1.1, 0.4],
        tags: ['hiphop', 'trap']
    },
    lofi_lazy: {
        pattern: [1.0, 0.4, 0.6, 0.5, 0.9, 0.4, 0.7, 0.5, 1.0, 0.4, 0.6, 0.5, 0.9, 0.4, 0.7, 0.5],
        tags: ['lofi', 'chill']
    },
    push_pull: {
        pattern: [1.1, 0.5, 0.9, 0.5, 1.1, 0.6, 0.8, 0.4, 1.1, 0.5, 0.9, 0.5, 1.1, 0.6, 0.8, 0.4],
        tags: ['experimental']
    },
    afrobeat: {
        pattern: [1.1, 0.5, 0.7, 1.0, 0.5, 0.5, 1.0, 0.5, 1.1, 0.5, 0.7, 1.0, 0.5, 0.5, 1.0, 0.5],
        tags: ['world', 'funk']
    }
};

export const RHYTHM_PATTERNS = {
    basic_rock: {
        kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['rock', 'pop']
    },
    rock_fill_1: {
        kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 1, 1, 1, 0, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        tags: ['rock', 'fill']
    },
    driving_rock: {
        kick: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['rock']
    },
    funky_rock: {
        kick: [1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 1, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1, 1, 0, 1, 1],
        tags: ['funk']
    },
    four_on_floor: {
        kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        tags: ['electronic', 'pop']
    },
    disco_groove: {
        kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 1], // Open hat finish
        tags: ['disco', 'pop']
    },
    latin_clave_3_2: {
        clave: [1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0, 0, 0],
        kick:  [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        snare: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        hats:  [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['latin']
    },
    bossa_nova: {
        kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0], // Cross stick
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0], // Shaker
        tags: ['latin', 'jazz']
    },
    reggae_one_drop: {
        kick: [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        tags: ['reggae']
    },
    breakbeat: {
        kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['electronic', 'breakbeat']
    },
    breakbeat_chopped: {
        kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 1, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0],
        tags: ['electronic', 'breakbeat', 'complex']
    },
    swing_jazz: {
        kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        hats: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        tags: ['jazz']
    },
    jazz_brushes: {
        kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        snare:[1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1], // Swirl
        hats: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        tags: ['jazz', 'soft']
    },
    metal_blast: {
        kick: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        snare:[0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['metal']
    },
    syncopated_rock: {
        kick: [1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0],
        tags: ['rock', 'alternative']
    },
    dnb_basic: {
        kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
        tags: ['dnb', 'electronic']
    },
    funk_syncopated: {
        kick: [1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 0],
        hats: [0, 0, 1, 0, 1, 0, 1, 1, 0, 0, 1, 0, 1, 0, 1, 1],
        tags: ['funk']
    },
    jazz_ride: {
        kick: [1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        snare:[0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        hats: [1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0],
        tags: ['jazz']
    },
    half_time_shuffle: {
        kick: [1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['pop', 'shuffle']
    },
    lofi_beat_1: {
        kick: [1, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 1, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['lofi']
    },
    lofi_beat_2: {
        kick: [1, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1],
        hats: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
        tags: ['lofi']
    },
    western_gallop: {
        kick: [1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0],
        snare:[0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
        hats: [1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0, 1, 0],
        tags: ['country', 'western']
    }
};

// Instrument Pitch Limits (MIDI Note Numbers)
export const INSTRUMENT_RANGES = {
    'Violin': { min: 55, max: 103 },
    'Viola': { min: 48, max: 91 },
    'Cello': { min: 36, max: 76 },
    'Bass': { min: 28, max: 67 }, // Double Bass / Electric Bass
    'Contrabass': { min: 28, max: 67 },
    'Guitar': { min: 40, max: 88 },
    'Piano': { min: 21, max: 108 },
    'Tuba': { min: 29, max: 58 },
    'Trumpet': { min: 52, max: 86 }, // Bb Trumpet actual
    'Horn': { min: 41, max: 77 }, // French Horn
    'Trombone': { min: 40, max: 72 },
    'Flute': { min: 60, max: 96 },
    'Clarinet': { min: 50, max: 94 },
    'Oboe': { min: 58, max: 91 },
    'Bassoon': { min: 34, max: 72 },
    'Synthesizer': { min: 12, max: 120 }, // Broad range
    'Drums': { min: 0, max: 127 } // Percussion maps vary
};
